import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';
import { supabase } from '@/lib/supabase';
import { useWishlist } from '@/hooks/useWishlist';

interface Product {
  id: string;
  name: string;
  brand: string;
  rating: number;
  price: number;
  original_price: number | null;
  image: string;
}

export default function Wishlist() {
  const { ids, remove } = useWishlist();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (ids.length === 0) {
      setProducts([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    supabase
      .from('products')
      .select('id,name,brand,rating,price,original_price,image')
      .in('id', ids)
      .then(({ data }) => {
        setProducts(data || []);
        setLoading(false);
      });
  }, [ids]);

  return (
    <div className="min-h-screen bg-white text-ink-900 antialiased">
      <Header />
      <main className="pt-28 md:pt-36 pb-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h1 className="font-display text-4xl md:text-5xl font-bold text-ink-900 tracking-tight">My Wishlist</h1>
          <p className="mt-4 text-lg text-ink-600 max-w-2xl leading-relaxed">
            Products you&rsquo;ve saved for later. Saved on this device only.
          </p>

          {loading ? (
            <div className="mt-16 text-center text-ink-400 text-sm">Loading...</div>
          ) : products.length === 0 ? (
            <div className="mt-16 text-center py-16">
              <span className="w-16 h-16 rounded-full bg-ink-50 flex items-center justify-center mx-auto text-ink-300 text-3xl">
                <i className="ri-heart-line"></i>
              </span>
              <p className="mt-4 text-ink-500">Your wishlist is empty.</p>
              <Link to="/search" className="mt-6 inline-flex h-10 px-5 rounded-lg bg-ink-900 hover:bg-ink-700 text-white text-sm font-semibold transition">
                Browse products
              </Link>
            </div>
          ) : (
            <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {products.map((p) => (
                <div key={p.id} className="product-card group relative bg-white rounded-2xl border border-ink-100 overflow-hidden flex flex-col">
                  <button
                    onClick={() => remove(p.id)}
                    aria-label="Remove from wishlist"
                    className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-white/90 backdrop-blur flex items-center justify-center text-ink-500 hover:text-rose-600 shadow-soft transition"
                  >
                    <i className="ri-close-line text-base"></i>
                  </button>
                  <Link to={`/product/${p.id}`} className="relative aspect-square bg-ink-50 overflow-hidden">
                    <img src={p.image} alt={p.name} className="product-image w-full h-full object-contain p-4" />
                  </Link>
                  <div className="p-4 flex flex-col flex-1">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">{p.brand}</p>
                    <Link to={`/product/${p.id}`} className="mt-1 font-semibold text-ink-900 text-sm leading-snug line-clamp-2 hover:text-cta-600 transition">
                      {p.name}
                    </Link>
                    <div className="mt-2 flex items-center gap-1.5 text-xs text-ink-600">
                      <i className="ri-star-fill text-amber-500"></i>
                      <span className="font-semibold text-ink-800">{p.rating}</span>
                    </div>
                    <div className="mt-auto pt-3 flex items-baseline gap-2">
                      <span className="font-display text-lg font-bold text-cta-600">${p.price}</span>
                      {p.original_price && p.original_price > p.price && (
                        <span className="text-xs text-ink-400 line-through">${p.original_price}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
